var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { AttributionControl, GeolocateControl, MapProvider, NavigationControl, ScaleControl } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import { states } from './us-bbox';
import { debounce } from 'lodash';
export default function BaseMap(_a) {
    var { globalConfig, mapConfig, layers, mapRef, children } = _a, mapProps = __rest(_a, ["globalConfig", "mapConfig", "layers", "mapRef", "children"]);
    const uiRef = useRef(null);
    const [inputOptions] = useState({
        dragRotate: false,
    });
    const defaultBounds = states[globalConfig.stateAbbr || 'CA'];
    const defaultViewState = mapProps.initialViewState || {
        padding: {
            left: 44,
            top: 44,
            right: 44,
            bottom: 44,
        }
    };
    const boundMap = () => {
        const map = mapRef.current;
        const newBounds = mapProps.currentBounds || defaultBounds;
        if (map && newBounds) {
            setTimeout(() => {
                map.fitBounds(newBounds, {
                    linear: true,
                    duration: 1500,
                });
            }, 0);
        }
    };
    const [viewState, setViewState] = useState(defaultViewState);
    useEffect(() => {
        boundMap();
    }, [mapProps.currentBounds]);
    useEffect(() => {
        boundMap();
    }, [mapProps.resetBounds]);
    useEffect(() => {
        boundMap();
    }, [viewState.paddingChanged]);
    const handleLoad = (event) => {
        const map = event.target;
        // Animated zoom in effect on page load
        if (map && defaultBounds) {
            updatePadding();
            map.fitBounds(defaultBounds, { animate: false });
            map.zoomOut({ animate: false });
            map.fitBounds(defaultBounds, {
                duration: 3000
            });
        }
        if (typeof mapProps.onLoad == 'function')
            mapProps.onLoad(event);
    };
    const updatePadding = () => {
        const ui = uiRef.current;
        const safeArea = ui === null || ui === void 0 ? void 0 : ui.querySelector('.EventPage__mapUiActiveCol');
        const uiClientRect = ui === null || ui === void 0 ? void 0 : ui.getBoundingClientRect();
        const safeClientRect = safeArea === null || safeArea === void 0 ? void 0 : safeArea.getBoundingClientRect();
        const uiDimensions = uiClientRect || { top: 0, right: 0, bottom: 0, left: 0 };
        const safeDimensions = safeClientRect || { top: 0, right: 0, bottom: 0, left: 0 };
        const newPadding = {
            bottom: uiDimensions.bottom - safeDimensions.bottom + 44,
            left: uiDimensions.left - safeDimensions.left + 44,
            right: uiDimensions.right - safeDimensions.right + 44,
            top: uiDimensions.top - safeDimensions.top + 44,
        };
        // If no padding changed stop the update
        if (viewState.padding.bottom == newPadding.bottom
            && viewState.padding.left == newPadding.left
            && viewState.padding.right == newPadding.right
            && viewState.padding.top == newPadding.top)
            return;
        setViewState(() => (Object.assign(Object.assign({}, viewState), { padding: {
                top: uiDimensions.top - safeDimensions.top + 44,
                right: uiDimensions.right - safeDimensions.right + 44,
                bottom: uiDimensions.bottom - safeDimensions.bottom + 44,
                left: uiDimensions.left - safeDimensions.left + 44,
            }, paddingChanged: viewState.paddingChanged + 1 })));
    };
    const handleResize = useMemo(() => debounce(updatePadding, 200), []);
    const onHover = useCallback((event) => {
        if (typeof mapProps.onHover == 'function')
            mapProps.onHover(event);
    }, []);
    const onMouseOut = useCallback((event) => {
        if (typeof mapProps.onMouseOut == 'function')
            mapProps.onMouseOut(event);
    }, []);
    return (React.createElement(MapProvider, null,
        React.createElement("div", { className: "EventPage p-3 px-md-3 py-md-0" },
            React.createElement("div", { className: "EventPage__mapBg" },
                React.createElement(Map, Object.assign({ ref: mapRef }, viewState, inputOptions, mapProps, { onMove: event => setViewState(event.viewState), onResize: handleResize, onMouseMove: onHover, onMouseOut: onMouseOut, onLoad: handleLoad, mapLib: maplibregl, mapStyle: mapConfig.vectorStyle || "https://api.maptiler.com/maps/basic-v2-light/style.json?key=UkL1VJnNaQc7rdAvfw5r" }),
                    Boolean(!mapConfig.vectorStyle) && (React.createElement(AttributionControl, { compact: true, customAttribution: "Using developer map, please define map.vectorStyle in the page config.", position: 'top-left' })),
                    React.createElement(GeolocateControl, { position: 'top-left' }),
                    React.createElement(ScaleControl, { unit: 'imperial', position: 'bottom-left' }),
                    React.createElement(NavigationControl, { position: 'top-left', showCompass: false }),
                    layers)),
            React.createElement("div", { ref: uiRef, className: "EventPage__mapUi h-100" }, children))));
}
